/* ==================================================================
   SABLE & OCHRE — IMMERSIVE CRAFT LAYER
   ------------------------------------------------------------------
   Loads last, after gsap / scrolltrigger / museum.js / theme.js /
   scroll-polish.js. Owns:

     1. viewport unit normalisation
     2. the entry threshold (gate) + drifting motes
     3. word-split staggers on headings museum.js does not already own
     4. the replaced cursor

   Nothing here rebuilds an existing room. Every effect degrades to
   "plain but working" if GSAP is absent, motion is reduced, or JS
   fails outright.
   ================================================================== */
(function () {
  'use strict';

  var root    = document.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ================================================================
     1. VIEWPORT UNITS
     ----------------------------------------------------------------
     100vh is a lie on mobile: innerHeight changes when the URL bar
     hides, so anything sized in vh jumps mid-scroll. Write real pixel
     values instead, and only recompute the height on a *width* change
     (an orientation change or a real resize) — never on the URL-bar
     shuffle that a scroll causes.
     ================================================================ */
  var lastWidth = 0;

  function measureViewport(force) {
    var w = window.innerWidth;
    if (!force && w === lastWidth) return;
    lastWidth = w;

    var h = window.innerHeight;
    var header = document.querySelector('.site-header');
    var headerH = header ? Math.round(header.getBoundingClientRect().height) : 0;

    root.style.setProperty('--hp-vw', (w / 100) + 'px');
    root.style.setProperty('--hp-svh', (h / 100) + 'px');
    root.style.setProperty('--hp-lvh', (h / 100) + 'px');
    root.style.setProperty('--hp-header', headerH + 'px');
    root.style.setProperty('--hp-stage', (h - headerH) + 'px');
  }

  /* ================================================================
     2. SPLIT TEXT
     ----------------------------------------------------------------
     Walks text nodes only, so inline markup inside a heading survives.
     inline-flex on the word keeps its glyphs together across a wrap.
     ================================================================ */
  function splitWords(el) {
    if (!el || el.dataset.hpSplitDone === '1') return [];
    var words = [];
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var nodes = [];
    var n;
    while ((n = walker.nextNode())) {
      if (n.nodeValue && n.nodeValue.trim()) nodes.push(n);
    }

    nodes.forEach(function (node) {
      var frag = document.createDocumentFragment();
      // Keep the whitespace runs as their own nodes so spacing is exact.
      node.nodeValue.split(/(\s+)/).forEach(function (part) {
        if (!part) return;
        if (/^\s+$/.test(part)) {
          frag.appendChild(document.createTextNode(part));
          return;
        }
        var span = document.createElement('span');
        span.className = 'hp-word';
        span.textContent = part;
        frag.appendChild(span);
        words.push(span);
      });
      node.parentNode.replaceChild(frag, node);
    });

    el.dataset.hpSplitDone = '1';
    return words;
  }

  /* Guarantee text can never be left invisible by a failed animation. */
  function failsafeReveal(el, ms) {
    setTimeout(function () { el.classList.add('hp-played'); }, ms || 2600);
  }

  /* ================================================================
     3. DRIFTING MOTES
     ----------------------------------------------------------------
     The reference floats paper shards through its threshold. Canvas 2D
     is plenty — a few dozen rotating rectangles at low opacity. Returns
     a stop() so the loop dies with the gate rather than leaking.
     ================================================================ */
  function startMotes(canvas) {
    if (!canvas || reduced) return function () {};
    var ctx = canvas.getContext('2d');
    if (!ctx) return function () {};

    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var w = 0, h = 0, shards = [], raf = 0, running = true;

    function resize() {
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    /* Palettes lifted from the paintings themselves: the foliage and the
       ranunculus/daisy/buttercup sprays that sit beside every animal. */
    var LEAF_COLOURS   = ['#8E9B6C', '#6E7A4F', '#B98A45', '#A5623A', '#D8BE86'];
    var PETAL_COLOURS  = ['#E7C3C8', '#F0E6D6', '#EEDCA0', '#D9A0A8', '#EFD9C2'];

    function pick(list) { return list[(Math.random() * list.length) | 0]; }

    /* A leaf: two mirrored curves meeting at tip and stem. */
    function drawLeaf(c, len, wid) {
      c.beginPath();
      c.moveTo(0, -len / 2);
      c.quadraticCurveTo(wid / 2, 0, 0, len / 2);
      c.quadraticCurveTo(-wid / 2, 0, 0, -len / 2);
      c.closePath();
      c.fill();
    }

    /* A blossom: petals swept round a centre, drawn as one path so the
       overlaps do not darken where they cross. */
    function drawFlower(c, r, petals) {
      /* One path per petal. Collecting them into a single path and filling once
         made the ring enclose its own middle under the nonzero rule, so every
         blossom rendered as a filled pentagon. */
      for (var i = 0; i < petals; i++) {
        var a = (i / petals) * Math.PI * 2;
        c.beginPath();
        c.ellipse(Math.cos(a) * r * 0.62, Math.sin(a) * r * 0.62,
                  r * 0.44, r * 0.25, a, 0, Math.PI * 2);
        c.fill();
      }
      c.beginPath();
      c.arc(0, 0, r * 0.24, 0, Math.PI * 2);
      c.fill();
    }

    function seed() {
      var count = Math.round(Math.min(46, Math.max(16, w / 34)));
      shards = [];
      for (var i = 0; i < count; i++) {
        var isLeaf = Math.random() < 0.62;
        shards.push({
          x: Math.random() * w,
          y: Math.random() * h,
          /* big enough that the silhouette is legible - below this a petal is
             barely a pixel and every particle reads as a dot */
          size: isLeaf ? (11 + Math.random() * 15) : (7 + Math.random() * 7),
          rot: Math.random() * Math.PI * 2,
          /* leaves tumble, blossoms turn lazily */
          spin: (Math.random() - 0.5) * (isLeaf ? 0.02 : 0.008),
          vx: (Math.random() - 0.5) * 0.22,
          vy: 0.06 + Math.random() * 0.28,
          /* unchanged from the paper-shard version, deliberately */
          alpha: 0.10 + Math.random() * 0.30,
          leaf: isLeaf,
          petals: 5 + ((Math.random() * 2) | 0),
          colour: isLeaf ? pick(LEAF_COLOURS) : pick(PETAL_COLOURS),
          /* the side-to-side drift that reads as "falling" rather than "sinking" */
          sway: Math.random() * Math.PI * 2,
          swaySpeed: 0.008 + Math.random() * 0.016,
          swayAmp: 0.25 + Math.random() * 0.85,
          /* only leaves turn edge-on as they go over */
          flip: Math.random() * Math.PI * 2,
          flipSpeed: isLeaf ? (0.006 + Math.random() * 0.014) : 0
        });
      }
    }

    function frame() {
      if (!running) return;
      ctx.clearRect(0, 0, w, h);
      for (var i = 0; i < shards.length; i++) {
        var s = shards[i];
        s.sway += s.swaySpeed;
        s.x += s.vx + Math.sin(s.sway) * s.swayAmp * 0.32;
        s.y += s.vy;
        s.rot += s.spin;
        if (s.flipSpeed) s.flip += s.flipSpeed;

        if (s.y - s.size > h) { s.y = -s.size; s.x = Math.random() * w; }
        if (s.x < -s.size) s.x = w + s.size;
        if (s.x > w + s.size) s.x = -s.size;

        ctx.save();
        ctx.translate(s.x, s.y);
        ctx.rotate(s.rot);
        /* squashing X is what sells the turn-over; floored so a leaf never
           vanishes completely into a zero-width sliver */
        if (s.flipSpeed) {
          var f = Math.abs(Math.cos(s.flip));
          ctx.scale(0.25 + f * 0.75, 1);
        }
        ctx.globalAlpha = s.alpha;
        ctx.fillStyle = s.colour;
        if (s.leaf) { drawLeaf(ctx, s.size, s.size * 0.54); }
        else { drawFlower(ctx, s.size, s.petals); }
        ctx.restore();
      }
      raf = requestAnimationFrame(frame);
    }

    function onResize() { resize(); seed(); }

    resize();
    seed();
    frame();
    window.addEventListener('resize', onResize);

    return function stop() {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
    };
  }

  /* ================================================================
     4. THE ENTRY THRESHOLD
     ----------------------------------------------------------------
     Homepage only, every visit. Scroll is already locked by the inline
     flag in theme.liquid so the shop never flashes behind it.

     On close we must call ScrollTrigger.refresh(): the pinned corridor
     in museum.js measures against a scrollable document, and while the
     gate held overflow:hidden those measurements were taken against a
     frozen page.
     ================================================================ */
  function initGate() {
    var gate = document.querySelector('.hp-gate');
    if (!gate) { root.classList.remove('hp-gate-open'); return; }

    // Tells the inline fail-open timer in theme.liquid to stand down:
    // the gate is under control and will close itself properly.
    root.classList.add('hp-gate-live');

    var enterBtn = gate.querySelector('.hp-gate-enter');
    var skipBtn  = gate.querySelector('.hp-gate-skip');
    var title    = gate.querySelector('.hp-gate-title');
    var closed   = false;

    var stopMotes = startMotes(gate.querySelector('.hp-gate-motes'));

    /* Word-by-word entrance on the title. The house name above it rides
       the plain CSS `is-ready` transition — two competing staggers on
       stacked lines read as jitter rather than choreography. */
    if (title && !reduced && window.gsap) {
      var titleWords = splitWords(title);
      if (titleWords.length) {
        gsap.set(titleWords, { yPercent: 108, opacity: 0 });
        gsap.to(titleWords, {
          yPercent: 0,
          opacity: 1,
          duration: 1.0,
          ease: 'power3.out',
          stagger: 0.05,
          delay: 0.42
        });
      }
    }

    requestAnimationFrame(function () {
      requestAnimationFrame(function () { gate.classList.add('is-ready'); });
    });

    if (enterBtn) {
      try { enterBtn.focus({ preventScroll: true }); } catch (e) { enterBtn.focus(); }
    }

    /* Focus trap — while a modal is open, Tab must not reach the shop. */
    function onKeydown(e) {
      if (closed) return;
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key !== 'Tab') return;

      var focusables = gate.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])');
      if (!focusables.length) return;
      var first = focusables[0];
      var last  = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }

    function close() {
      if (closed) return;
      closed = true;

      document.removeEventListener('keydown', onKeydown, true);
      gate.classList.add('is-leaving');
      root.classList.remove('hp-gate-open');
      gate.setAttribute('aria-hidden', 'true');

      var main = document.getElementById('main-content');
      if (main) {
        var h = main.querySelector('h1, [tabindex], a, button');
        if (h) { try { h.focus({ preventScroll: true }); } catch (e) {} }
      }

      var done = function () {
        gate.classList.add('is-gone');
        stopMotes();
        // The document is scrollable again — let ScrollTrigger re-measure
        // before the corridor pin is ever reached.
        if (window.ScrollTrigger) ScrollTrigger.refresh();
        document.dispatchEvent(new CustomEvent('hp:enter'));
      };

      gate.addEventListener('transitionend', function once(ev) {
        if (ev.target !== gate) return;
        gate.removeEventListener('transitionend', once);
        done();
      });
      // Belt and braces: fire regardless if the transition never lands.
      setTimeout(done, reduced ? 400 : 1400);
    }

    if (enterBtn) enterBtn.addEventListener('click', close);
    if (skipBtn)  skipBtn.addEventListener('click', close);
    document.addEventListener('keydown', onKeydown, true);
  }

  /* ================================================================
     5. SITE-WIDE HEADING STAGGER
     ----------------------------------------------------------------
     Only headings museum.js does not already reveal, and never the
     entrance (whose words are split in Liquid and animated by
     scroll-polish.js). That leaves the collection title, the product
     title, the corridor and the featured grid.
     ================================================================ */
  function initHeadings() {
    var hasGsap = !!(window.gsap && window.ScrollTrigger);
    var candidates = document.querySelectorAll(
      '.mw-heading:not([data-mreveal]), [data-hp-split]'
    );

    Array.prototype.forEach.call(candidates, function (el) {
      if (el.closest('.mw-entrance') || el.closest('.hp-gate')) return;

      var words = splitWords(el);
      if (!words.length) return;
      el.classList.add('hp-split');

      if (reduced || !hasGsap) {
        el.classList.add('hp-played');
        return;
      }

      failsafeReveal(el, 2600);

      gsap.set(words, { yPercent: 104, opacity: 0 });
      gsap.to(words, {
        yPercent: 0,
        opacity: 1,
        duration: 0.85,
        ease: 'power3.out',
        stagger: 0.05,
        scrollTrigger: { trigger: el, start: 'top 88%', once: true },
        onStart: function () { el.classList.add('hp-played'); }
      });
    });
  }

  /* ================================================================
     6. REPLACED CURSOR
     ----------------------------------------------------------------
     A lerped ring plus a 1:1 dot, opening up over anything clickable.
     Pointer-fine only. The native cursor is restored on typing
     surfaces by CSS — losing the I-beam in a checkout field is a real
     cost for a cosmetic gain.
     ================================================================ */
  function initCursor() {
    if (!finePointer || reduced) return;

    var ring = document.createElement('div');
    ring.className = 'hp-cursor';
    document.body.appendChild(ring);

    var tx = window.innerWidth / 2, ty = window.innerHeight / 2;
    var rx = tx, ry = ty;

    // The native cursor is always visible, so the ring waits for the first
    // mousemove and starts ON the pointer. Showing it straight away parked a
    // stray circle in the middle of the screen until the mouse moved, then
    // dragged it across the page.
    var started = false;
    document.addEventListener('mousemove', function (e) {
      tx = e.clientX; ty = e.clientY;
      if (!started) { started = true; rx = tx; ry = ty; }
      if (!ring.classList.contains('is-live')) ring.classList.add('is-live');
    }, { passive: true });

    document.documentElement.addEventListener('mouseleave', function () {
      ring.classList.remove('is-live');
    });

    // State comes from what the pointer is over NOW. The old add-on-over /
    // remove-on-out pair flickered: crossing from a link's text into its
    // image fires mouseout on the first child (ring shrinks) and mouseover
    // on the next (ring grows), so the ring pulsed over every card.
    var INTERACTIVE = 'a, button, [role="button"], input, textarea, select, summary, label, .mw-piece, .mw-artwork, .mw-ocean-tile';
    document.addEventListener('mouseover', function (e) {
      var t = e.target;
      ring.classList.toggle('is-active', !!(t && t.closest && t.closest(INTERACTIVE)));
    }, { passive: true });

    (function loop() {
      rx += (tx - rx) * 0.22;
      ry += (ty - ry) * 0.22;
      ring.style.transform = 'translate3d(' + rx.toFixed(1) + 'px,' + ry.toFixed(1) + 'px,0)';
      requestAnimationFrame(loop);
    }());
  }

  /* ================================================================ */
  function init() {
    measureViewport(true);
    window.addEventListener('resize', function () { measureViewport(false); }, { passive: true });
    window.addEventListener('orientationchange', function () { measureViewport(true); });

    initGate();
    initHeadings();
    // initCursor(); - ring removed 2026-10-07 at the owner's request (it still misbehaved).
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());
